import { useEffect, useRef, useState } from 'react';
import { parquetReadObjects } from 'hyparquet';
import { decompress } from 'fzstd';
import { lloydConstants } from './Constants';
import { useYearContext } from './context/YearContext';

// The data files are zstd-compressed parquet and nothing else, so only that codec
// ships. hyparquet-compressors would bring snappy, brotli (with its 80KB
// dictionary), gzip and lz4 along for the ride.
const compressors = {
	ZSTD: (input, outputLength) => decompress(input, new Uint8Array(outputLength)),
};

const waveUrl = (year) => `data/resilience_${year}.parquet`;

// One fetch per file. hyparquet's own URL reader probes the length and then reads
// the footer and each row group by range request, which is three round trips for
// files under a megabyte. The whole file in one go is cheaper, and an ArrayBuffer
// already satisfies the slice/byteLength interface hyparquet reads through.
const inflight = new Map();

export const prefetchWave = (year) => {
	if (!inflight.has(year)) {
		inflight.set(
			year,
			fetch(waveUrl(year)).then((response) => {
				if (!response.ok) throw new Error(`${waveUrl(year)}: ${response.status}`);
				return response.arrayBuffer();
			})
		);
	}

	return inflight.get(year);
};

const loadYear = async (year) => {
	const file = await prefetchWave(year);
	const rows = await parquetReadObjects({ file, compressors });

	// Rows are kept as they come off the file. Country name, region and income level
	// are looked up once per country at aggregation time rather than written onto
	// every one of 140,000 rows per wave. The only rows discarded are those from a
	// country the lookup cannot name, which the build script warns about.
	const known = lloydConstants.countryCodeToName;
	const kept = rows.filter((row) => known[row.country_code] !== undefined);

	performance.mark(`resilience:wave-ready:${year}`);
	return kept;
};

export const useData = () => {
	const { selectedYear, comparisonYear, yearlyData, setYearlyDataForYear } = useYearContext();
	const [loading, setLoading] = useState(true);
	const requested = useRef(new Set());

	const hasSelectedYear = Boolean(yearlyData[selectedYear]);

	useEffect(() => {
		// The wave on screen and the one it is compared against are needed now; the rest
		// can arrive while the user reads the chart.
		const urgent = [selectedYear, comparisonYear].filter(
			(year) => typeof year === 'number' && !requested.current.has(year)
		);
		const rest = lloydConstants.waves.filter(
			(year) => !requested.current.has(year) && !urgent.includes(year)
		);

		[...urgent, ...rest].forEach((year) => requested.current.add(year));

		const fetchAll = async () => {
			await Promise.all(
				urgent.map(async (year) => setYearlyDataForYear(year, await loadYear(year)))
			);

			rest.forEach(async (year) => setYearlyDataForYear(year, await loadYear(year)));
		};

		fetchAll();
	}, [selectedYear, comparisonYear, setYearlyDataForYear]);

	useEffect(() => {
		if (hasSelectedYear) setLoading(false);
	}, [hasSelectedYear]);

	return { loading: loading || !hasSelectedYear };
};
